const getEditedThumbnail = (imageLink: string): string => {
  return [imageLink, '?tr=h-300,w-300'].join('');
};

/**
 * ImageKit renders a still from any video by appending /ik-thumbnail.jpg to the
 * video URL, so posters and scrub previews are pure URL derivation — no upload
 * step, no stored thumbnail, and it works for videos already posted.
 *
 * The frame is chosen with the `so` (start offset, in seconds) transformation.
 */
const buildVideoThumbnail = (
  videoUrl: string,
  transforms: string[]
): string | null => {
  if (!videoUrl) return null;

  // Only ImageKit-hosted media can be transformed this way. Anything else (a
  // blob: preview, an external URL) has no derivable thumbnail.
  if (!videoUrl.includes('ik.imagekit.io')) return null;

  // A query string would land before /ik-thumbnail.jpg and break the path.
  const [path] = videoUrl.split('?');
  const base = `${path}/ik-thumbnail.jpg`;

  // Pin the output format. Left to choose, ImageKit returns PNG for some
  // widths and JPEG for others — w-480 came back as a 287KB PNG where w-800
  // was a 25KB JPEG. f-jpg makes the payload scale with the width, as expected.
  const all = [...transforms, 'f-jpg'];

  return `${base}?tr=${all.join(',')}`;
};

/**
 * Poster frame for a video.
 *
 * Defaults to one second in rather than the true first frame, which is very
 * often black. Videos shorter than that keep the default first frame, since
 * seeking past the end yields nothing.
 *
 * The width cap is not just for layout: an `so` offset with no size constraint
 * makes ImageKit return the raw frame as a full-resolution PNG (~940KB on the
 * demo asset, vs ~25KB once a width is given).
 */
export const getVideoPoster = (
  videoUrl: string,
  durationSeconds?: number,
  width = 800
): string | null => {
  const canOffset = durationSeconds === undefined || durationSeconds > 1;
  const transforms = canOffset ? ['so-1'] : [];
  transforms.push(`w-${width}`);
  return buildVideoThumbnail(videoUrl, transforms);
};

/**
 * Small still at a given second, for the scrub-bar hover/drag preview.
 * Width-capped because these are rendered as ~160px tooltips.
 */
export const getVideoPreviewFrame = (
  videoUrl: string,
  second: number,
  width = 160
): string | null => {
  // `so` takes whole seconds; a fractional offset is silently dropped.
  const offset = Math.max(0, Math.floor(second));
  return buildVideoThumbnail(videoUrl, [`so-${offset}`, `w-${width}`]);
};

/**
 * Square, CDN-resized avatar.
 *
 * `h` is not redundant alongside `w`: given width alone ImageKit keeps the
 * source aspect ratio (a 5232x3744 upload comes back 200x143), which the
 * circular frame would then crop arbitrarily. Passing both engages the default
 * maintain-ratio crop, which fills the square.
 *
 * `fo-auto` chooses that crop by content rather than from the centre, so
 * off-centre faces survive it. It is the only focus value worth passing here —
 * fo-face, fo-center and fo-top all silently fall back to the default crop.
 *
 * The format is deliberately left unpinned: ImageKit content-negotiates WebP
 * off the Accept header (~4KB vs ~5KB JPEG at this size), so pinning one could
 * only ever cost bytes or break a client that cannot decode it.
 *
 * `size` is the output edge in pixels, not CSS pixels — pick it to cover the
 * render size at the DPR you care about (default suits renders up to ~48px at
 * 3x). Avatars are rendered many-to-a-screen in the feed, so oversizing the
 * default is not free.
 */
export const getCircularAvatar = (imageLink: string, size = 160): string => {
  // Only ImageKit-hosted media can be transformed. Anything else (notably the
  // literal 'null' stored for users with no avatar) is handed back untouched.
  if (!imageLink || !imageLink.includes('ik.imagekit.io')) return imageLink;

  // A second tr param would be ignored, so respect a caller's own transform.
  if (/[?&]tr=/.test(imageLink)) return imageLink;

  const separator = imageLink.includes('?') ? '&' : '?';
  return `${imageLink}${separator}tr=w-${size},h-${size},fo-auto`;
};

export default getEditedThumbnail;
